import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Menu } from 'lucide-react';
import { PhoneFrame } from './PhoneFrame';
const cases = [{
  clinician: 'Dr. Marcus Holm',
  location: 'Stockholm',
  challenge: 'Deep margin elevation',
  img: 'https://images.unsplash.com/photo-1606265752439-1f18756aa5fc?w=600&auto=format&fit=crop&q=80'
}, {
  clinician: 'Dr. Priya Anand',
  location: 'Singapore',
  challenge: 'Posterior bulk-fill',
  img: 'https://images.unsplash.com/photo-1609840114035-3c981b782dfe?w=600&auto=format&fit=crop&q=80'
}, {
  clinician: 'Dr. Lucas Mendes',
  location: 'São Paulo',
  challenge: 'Diastema closure',
  img: 'https://images.unsplash.com/photo-1581585504370-4d2f73f1d3ce?w=600&auto=format&fit=crop&q=80'
}, {
  clinician: 'Dr. Yuki Tanaka',
  location: 'Tokyo',
  challenge: 'Class V cervical',
  img: 'https://images.unsplash.com/photo-1571772996211-2f02c9727629?w=600&auto=format&fit=crop&q=80'
}];
export function LayerMenu() {
  return <section className="py-24 bg-solventum-dark" data-id="element-173">
      <div className="max-w-7xl mx-auto px-6" data-id="element-174">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16" data-id="element-175">
          <div className="flex items-center gap-3 mb-4" data-id="element-176">
            <div className="bg-solventum-green text-solventum-dark px-4 py-1 rounded-full text-sm font-bold" data-id="element-177">
              LAYER 4
            </div>
            <Menu className="w-5 h-5 text-solventum-green" data-id="element-178" />
            <span className="text-solventum-lightTeal/60 text-xs tracking-[0.2em] uppercase" data-id="element-179">
              Mobile · Library
            </span>
          </div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-180">
            The Case Library
          </h2>
          <p className="text-xl text-solventum-lightTeal/80 max-w-3xl" data-id="element-181">
            Browse by clinician you trust or the clinical challenge you're
            facing tomorrow — designed for quick chair-side discovery, with easy
            access to all resources.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.7
      }} className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 bg-gradient-to-br from-[#01403A] via-solventum-dark to-[#011A16] py-16 px-6" data-id="element-182">
          <div className="absolute inset-0 opacity-50" style={{
          backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(28,207,147,0.2) 0%, transparent 55%)'
        }} data-id="element-183" />

          <div className="relative grid lg:grid-cols-[1fr_auto_1fr] gap-10 items-center" data-id="element-184">
            <div className="hidden lg:block space-y-6 text-right" data-id="element-185">
              <FeatureRow title="Two ways in" body="Pivot between clinician and clinical challenge with a single tap." data-id="element-186" />
            </div>

            <PhoneFrame data-id="element-187">
              <CaseLibraryScreen data-id="element-188" />
            </PhoneFrame>

            <div className="hidden lg:block space-y-6" data-id="element-189">
              <FeatureRow title="Card-first browsing" body="Hero card spotlights a new release; grid below for fast scanning." align="left" data-id="element-190" />
              <FeatureRow title="Thumbprint filters" body="Topic chips sit within thumb reach — single-handed exploration." align="left" data-id="element-191" />
            </div>
          </div>
        </motion.div>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center text-solventum-lightTeal/60 mt-12 italic" data-id="element-192">
          Two ways in: by clinician you trust, or by case you face tomorrow.
        </motion.p>
      </div>
    </section>;
}
function FeatureRow({
  title,
  body,
  align = 'right'
}: {
  title: string;
  body: string;
  align?: 'left' | 'right';
}) {
  return <div className={align === 'right' ? 'ml-auto max-w-[240px]' : 'max-w-[240px]'} data-id="element-193">
      <div className="text-solventum-green text-[10px] font-semibold tracking-[0.25em] uppercase mb-2" data-id="element-194">
        {title}
      </div>
      <div className="text-solventum-lightTeal/70 text-sm leading-relaxed" data-id="element-195">
        {body}
      </div>
    </div>;
}
function UpNextCountdown() {
  const [count, setCount] = useState(5);
  useEffect(() => {
    const id = setInterval(() => {
      setCount(c => c <= 1 ? 5 : c - 1);
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const radius = 13;
  const circumference = 2 * Math.PI * radius;
  // progress from full (5) to empty (1)
  const progress = count / 5;
  return <div className="relative w-8 h-8 flex items-center justify-center" data-id="element-196">
      <svg className="absolute inset-0 -rotate-90" viewBox="0 0 32 32" width="32" height="32" data-id="element-197">
        <circle cx="16" cy="16" r={radius} fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="2" data-id="element-198" />
        <motion.circle cx="16" cy="16" r={radius} fill="none" stroke="#05DD4D" strokeWidth="2" strokeLinecap="round" strokeDasharray={circumference} animate={{
        strokeDashoffset: circumference * (1 - progress)
      }} transition={{
        duration: 0.9,
        ease: 'linear'
      }} data-id="element-199" />
      </svg>
      <span className="relative text-white text-[10px] font-bold font-mono leading-none" data-id="element-200">
        {count}
      </span>
    </div>;
}
function CaseLibraryScreen() {
  return <div className="relative w-full h-full overflow-hidden bg-solventum-dark" data-id="element-201">
      {/* App header — logo only */}
      <div className="px-5 pt-3 pb-3 flex items-center justify-center" data-id="element-202">
        <img src="https://cdn.magicpatterns.com/uploads/397WHAHW6jsh1YxDJrtm7i/solventum-logo-horz-clr-rev-rgb.png" alt="Solventum" className="h-4 w-auto" data-id="element-203" />
      </div>

      {/* Greeting */}
      <div className="px-5 mb-3" data-id="element-204">
        <div className="text-white/50 text-[10px] tracking-[0.2em] uppercase mb-1" data-id="element-205">
          Recommended
        </div>
        <div className="text-white font-editorial text-lg font-bold leading-tight" data-id="element-206">
          If you liked this, check out:
        </div>
      </div>

      {/* Hero featured card */}
      <div className="px-5 mb-4" data-id="element-207">
        <div className="relative h-32 rounded-xl overflow-hidden" data-id="element-208">
          <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?w=600&auto=format&fit=crop&q=80')"
        }} data-id="element-209" />
          <div className="absolute inset-0 bg-gradient-to-tr from-solventum-dark via-solventum-dark/40 to-transparent" data-id="element-210" />
          <div className="absolute top-2 left-2 px-1.5 py-0.5 bg-solventum-green text-solventum-dark text-[8px] font-bold rounded" data-id="element-211">
            NEW
          </div>
          <div className="absolute bottom-2 left-2.5 right-2.5" data-id="element-212">
            <div className="text-solventum-green text-[8px] font-bold tracking-[0.2em] uppercase mb-0.5" data-id="element-213">
              Featured · Anterior
            </div>
            <div className="text-white text-[13px] font-bold leading-tight mb-0.5" data-id="element-214">
              Dr Chris O'Connor
            </div>
            <div className="text-white/70 text-[10px]" data-id="element-215">
              Anterior fracture · layering
            </div>
          </div>
          <div className="absolute bottom-2 right-2" data-id="element-216">
            <UpNextCountdown data-id="element-217" />
          </div>
        </div>
      </div>

      {/* Section title */}
      <div className="px-5 mb-2" data-id="element-218">
        <div className="text-white text-[11px] font-bold tracking-wider uppercase" data-id="element-219">
          Featured Cases
        </div>
      </div>

      {/* Case grid */}
      <div className="px-5 grid grid-cols-2 gap-2" data-id="element-220">
        {cases.slice(0, 2).map(c => <div key={c.clinician} className="relative aspect-[3/4] rounded-lg overflow-hidden bg-white/5" data-id="element-221">
            <div className="absolute inset-0 bg-cover bg-center" style={{
          backgroundImage: `url(${c.img})`
        }} data-id="element-222" />
            <div className="absolute inset-0 bg-gradient-to-t from-solventum-dark via-solventum-dark/30 to-transparent" data-id="element-223" />
            <div className="absolute bottom-1.5 left-1.5 right-1.5" data-id="element-224">
              <div className="text-white text-[9px] font-semibold leading-tight truncate" data-id="element-225">
                {c.clinician}
              </div>
              <div className="text-solventum-medTeal text-[8px] truncate" data-id="element-226">
                {c.challenge}
              </div>
            </div>
          </div>)}

        {/* CTA tile — Explore more from Solventum */}
        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-white/[0.06] border border-white/10 flex flex-col items-center justify-center px-2 py-3" data-id="element-227">
          <img src="https://cdn.magicpatterns.com/uploads/397WHAHW6jsh1YxDJrtm7i/solventum-logo-horz-clr-rev-rgb.png" alt="Solventum" className="w-3/4 h-auto mb-2" data-id="element-228" />
          <div className="text-white text-[9px] font-semibold leading-tight text-center" data-id="element-229">
            Explore more
            <br data-id="element-230" />
            from Solventum
          </div>
        </div>

        {/* CTA tile — Book a demo */}
        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-solventum-green flex flex-col items-center justify-center px-2 py-3" data-id="element-231">
          <img src="https://cdn.magicpatterns.com/uploads/397WHAHW6jsh1YxDJrtm7i/solventum-logo-horz-clr-rev-rgb.png" alt="Solventum" className="w-3/4 h-auto mb-2 brightness-0" data-id="element-232" />
          <div className="text-solventum-dark text-[10px] font-bold leading-tight text-center" data-id="element-233">
            Book a demo
          </div>
        </div>
      </div>
    </div>;
}